import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import classnames from 'classnames'
import choosePane from './panes/choosePane'
import SubTools from './SubTools'
// import AnalysisContainer from 'components/analysis/AnalysisContainer'
import { withTranslation } from 'react-i18next'
import PopupMenu from '../../components/common/PopupMenu'
import Toolbar from './toolbar/Toolbar'
import ChevLeftIcon from '../common/icons/ChevLeftIcon'
import MapControls from '../map/settings/MapControls'
import MapItemControls from './MapItemControls'
import MapData from './MapData'
import UpgradeToPro from './UpgradeToPro'
import MultiSelectInstructions from './MultiSelectInstructions'
import PoiTooltip from '../common/PoiTooltip'
import { selectionTypes } from 'constants.js'

const isCreationPane = (selectionStore) => {
  if (!selectionStore) return false
  const { type, item, data } = selectionStore
  if (
    type === selectionTypes.NEW_ITEM ||
    type === selectionTypes.NEW_TARGET_GROUP ||
    type === 'FOLDER_ACTIONS' ||
    type === selectionTypes.USER_SHAPEFILE_LIST ||
    type === selectionTypes.USER_MARKERFILE_LIST
  ) {
    return true
  }
  if (
    type === selectionTypes.POINT_BUFFER ||
    type === selectionTypes.SHAPE ||
    type === selectionTypes.LINE ||
    type === selectionTypes.ISOCHRONE
  ) {
    return !item || !item.isUpdating
  }
  if (type === selectionTypes.MARKER) {
    return !data || !data.edit
  }
  if (
    type === selectionTypes.BULK_POINT_BUFFER ||
    type === selectionTypes.BULK_ISOCHRONE
  ) {
    return true
  }
  return false
}

@observer
class Tools extends Component {
  constructor (props) {
    super(props)
    this.toolsRef = React.createRef()
    this.updatePosition = this.updatePosition.bind(this)
  }

  componentDidMount () {
    // Set up scroll and resize listeners to update tooltip position
    window.addEventListener('scroll', this.updatePosition, true)
    window.addEventListener('resize', this.updatePosition)
  }

  componentWillUnmount () {
    // Clean up event listeners
    window.removeEventListener('scroll', this.updatePosition, true)
    window.removeEventListener('resize', this.updatePosition)
  }

  updatePosition () {
    const { uiStore } = this.props
    if (!uiStore.hoveredTargetGroupId || !this.toolsRef.current) return

    const buttonElement = document.querySelector(`.target-group__btn[data-group-id="${uiStore.hoveredTargetGroupId}"]`)
    if (buttonElement && this.toolsRef.current) {
      uiStore.updateHoveredTargetGroupPosition(buttonElement, this.toolsRef.current)
    }
    // If button not found, don't clear position - it might just be a timing issue
  }

  render () {
    const { uiStore, selectionStore, dataStore, rightSidebarStore } = this.props
    const view = choosePane(selectionStore)
    const isSubToolsOpen = view !== null && selectionStore.showSubTools && !uiStore.sideMenuHidden
    const isCreation = isCreationPane(selectionStore)

    // Find the hovered target group (check direct reference, displayGroups, owned and shared)
    const hoveredGroup = uiStore.hoveredTargetGroup ||
      (uiStore.hoveredTargetGroupId
        ? ((uiStore.displayGroups && uiStore.displayGroups.find(g => g && String(g.pk) === String(uiStore.hoveredTargetGroupId))) ||
          (dataStore.targetGroups && dataStore.targetGroups.find(g => g && String(g.pk) === String(uiStore.hoveredTargetGroupId))) ||
          (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => g && String(g.pk) === String(uiStore.hoveredTargetGroupId))))
        : null)

    const realTargetGroup = hoveredGroup && hoveredGroup.pk && dataStore.targetGroups
      ? (dataStore.targetGroups.find(g => g && String(g.pk) === String(hoveredGroup.pk)) ||
        (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => g && String(g.pk) === String(hoveredGroup.pk))) ||
        hoveredGroup)
      : hoveredGroup

    const targetMarkers = (hoveredGroup && Array.isArray(hoveredGroup.savedMarkers) && hoveredGroup.savedMarkers.length > 0)
      ? hoveredGroup.savedMarkers
      : (realTargetGroup && Array.isArray(realTargetGroup.savedMarkers) ? realTargetGroup.savedMarkers : [])
    const generalCount = targetMarkers.filter(m => m && m.is_general).length
    const allyCount = targetMarkers.filter(m => m && m.is_ally).length
    const competitorCount = targetMarkers.filter(m => m && m.is_competitor).length

    const shapesCount = (() => {
      if (!hoveredGroup) return 0
      const hg = hoveredGroup
      const rg = realTargetGroup || {}

      const geos = Math.max(
        Array.isArray(hg.geopolygons) ? hg.geopolygons.length : 0,
        Array.isArray(rg.geopolygons) ? rg.geopolygons.length : 0,
        Array.isArray(hg.geo_polygons) ? hg.geo_polygons.length : 0,
        Array.isArray(rg.geo_polygons) ? rg.geo_polygons.length : 0
      )
      const boundaries = Math.max(
        Array.isArray(hg.boundaries) ? hg.boundaries.length : 0,
        Array.isArray(rg.boundaries) ? rg.boundaries.length : 0,
        Array.isArray(hg.admin_boundaries) ? hg.admin_boundaries.length : 0,
        Array.isArray(rg.admin_boundaries) ? rg.admin_boundaries.length : 0
      )
      const zipCodes = Math.max(
        Array.isArray(hg.zipCodeBoundaries) ? hg.zipCodeBoundaries.length : 0,
        Array.isArray(rg.zipCodeBoundaries) ? rg.zipCodeBoundaries.length : 0,
        Array.isArray(hg.zipCodes) ? hg.zipCodes.length : 0,
        Array.isArray(rg.zipCodes) ? rg.zipCodes.length : 0,
        Array.isArray(hg.zip_code_boundaries) ? hg.zip_code_boundaries.length : 0,
        Array.isArray(rg.zip_code_boundaries) ? rg.zip_code_boundaries.length : 0,
        Array.isArray(hg.zip_codes) ? hg.zip_codes.length : 0,
        Array.isArray(rg.zip_codes) ? rg.zip_codes.length : 0
      )
      const shapes = Math.max(
        Array.isArray(hg.shapes) ? hg.shapes.length : 0,
        Array.isArray(rg.shapes) ? rg.shapes.length : 0
      )

      const markers = Math.max(
        targetMarkers.length,
        Array.isArray(hg.savedMarkers) ? hg.savedMarkers.length : 0,
        Array.isArray(rg.savedMarkers) ? rg.savedMarkers.length : 0,
        Array.isArray(hg.markers) ? hg.markers.length : 0,
        Array.isArray(rg.markers) ? rg.markers.length : 0,
        typeof hg.markersCount === 'number' ? hg.markersCount : 0,
        typeof rg.markersCount === 'number' ? rg.markersCount : 0
      )

      const computedRegionsCount = Math.max(
        typeof hg.regionsCount === 'number' ? hg.regionsCount : 0,
        typeof rg.regionsCount === 'number' ? rg.regionsCount : 0,
        Array.isArray(hg.regions) ? hg.regions.length : 0,
        Array.isArray(rg.regions) ? rg.regions.length : 0
      )

      const computedItemsCount = Math.max(
        typeof hg.itemsCount === 'number' ? hg.itemsCount : 0,
        typeof rg.itemsCount === 'number' ? rg.itemsCount : 0
      )

      const totalShapes = Math.max(geos + boundaries + zipCodes + shapes, computedRegionsCount)
      return Math.max(totalShapes + markers, computedItemsCount)
    })()

    const population = (() => {
      if (
        rightSidebarStore &&
        hoveredGroup &&
        (String(rightSidebarStore.targetGroupId) === String(hoveredGroup.pk)) &&
        rightSidebarStore.totalPopulation != null &&
        rightSidebarStore.totalPopulation > 0
      ) {
        return rightSidebarStore.totalPopulation
      }
      const realPop = realTargetGroup && realTargetGroup.landscanLatestPopulation
      if (realPop != null && realPop > 0) return realPop
      return hoveredGroup && hoveredGroup.landscanLatestPopulation != null ? hoveredGroup.landscanLatestPopulation : null
    })()

    const isPopupMenuOpen = !!(selectionStore.menuItem && selectionStore.menuItem.visible)
    const shouldShowHoveredTargetGroup = !isPopupMenuOpen &&
      !uiStore.showClickTooltip &&
      !uiStore.showDeletePrompt &&
      !uiStore.isAddChoiceTypeVisible &&
      hoveredGroup &&
      uiStore.hoveredTargetGroupPosition

    return (
      <div className='tools-container'>
        <div
          ref={this.toolsRef}
          className={classnames({
            tools: true,
            'has-sub-tools': view !== null,
            'tools--hidden': uiStore.sideMenuHidden,
          })}
        >
          <Toolbar />
          <MapItemControls />
          <MapData />
          <MapControls />
          {uiStore.user && uiStore.user.type === 'starter' &&
            <UpgradeToPro />}
          <MultiSelectInstructions />
          {
            uiStore.isHideMenuBtnVisible && !isSubToolsOpen && !isCreation && (
              <button type='button' className={classnames({ 'hide-menu-button': true, 'hide-menu-button--hidden': uiStore.sideMenuHidden })} onClick={() => uiStore.toggleSideBarMenu()}>
                <ChevLeftIcon />
              </button>
            )
          }
          {shouldShowHoveredTargetGroup && (
            <div
              ref={(el) => {
                if (el && this.toolsRef.current) {
                  // Find the poi tooltip container and adjust its position
                  setTimeout(() => {
                    const tooltipContainer = el.querySelector('.poi-tooltip__container')
                    if (!tooltipContainer) return
                    const tooltipRect = tooltipContainer.getBoundingClientRect()
                    const viewportHeight = window.innerHeight
                    const margin = 10
                    const toolsRect = this.toolsRef.current.getBoundingClientRect()
                    // Check if bottom overflows viewport
                    if (tooltipRect.bottom > viewportHeight - margin) {
                      const tooltipHeight = tooltipRect.height
                      const maxBottomInViewport = viewportHeight - margin
                      // Calculate new wrapper top position
                      const newWrapperTop = maxBottomInViewport - toolsRect.top - (tooltipHeight / 2)
                      // Ensure it doesn't go above the tools container
                      const minTop = margin
                      const adjustedWrapperTop = Math.max(minTop, newWrapperTop)
                      // Update wrapper position
                      el.style.top = `${adjustedWrapperTop}px`
                      // Force reflow and re-measure
                      let _unused = el.offsetHeight
                      const updatedRect = tooltipContainer.getBoundingClientRect()
                      // If still overflowing, change transform to translateY(0) and recalculate
                      if (updatedRect.bottom > viewportHeight - margin) {
                        el.style.transform = 'translateY(0)'
                        const newTopWithNoTransform = maxBottomInViewport - toolsRect.top - tooltipHeight
                        el.style.top = `${Math.max(margin, newTopWithNoTransform)}px`
                        // Final check - if still overflowing, adjust container position
                        _unused = el.offsetHeight
                        if (_unused) {
                          const finalRect = tooltipContainer.getBoundingClientRect()
                          if (finalRect.bottom > viewportHeight - margin) {
                            const overflow = finalRect.bottom - (viewportHeight - margin)
                            const currentTop = parseFloat(el.style.top) || 0
                            el.style.top = `${currentTop - overflow}px`
                          }
                        }
                      }
                    }
                  }, 0)
                }
              }}
              onMouseEnter={() => {
                // Keep tooltip visible when hovering over it
                if (uiStore && typeof uiStore.cancelClearHoveredTargetGroup === 'function') {
                  uiStore.cancelClearHoveredTargetGroup()
                }
              }}
              onMouseLeave={() => {
                if (uiStore && typeof uiStore.scheduleClearHoveredTargetGroup === 'function') {
                  uiStore.scheduleClearHoveredTargetGroup(250)
                } else if (uiStore && typeof uiStore.clearHoveredTargetGroup === 'function') {
                  uiStore.clearHoveredTargetGroup()
                }
              }}
              style={{
                position: 'absolute',
                top: `${uiStore.hoveredTargetGroupPosition.top}px`,
                left: `${uiStore.hoveredTargetGroupPosition.left}px`,
                zIndex: 1000,
                transform: 'translateY(-50%)',
                pointerEvents: 'auto'
              }}
            >
              <PoiTooltip
                isRightPaneVisible
                variant='target-group'
                name={hoveredGroup.name}
                population={population}
                shapesCount={shapesCount}
                generalCount={generalCount}
                allyCount={allyCount}
                competitorCount={competitorCount}
                onMouseLeaveTooltip={() => {
                  if (uiStore && typeof uiStore.clearHoveredTargetGroup === 'function') {
                    uiStore.clearHoveredTargetGroup()
                  }
                }}
                onViewMore={async () => {
                  selectionStore.select(hoveredGroup)
                  uiStore.isPOIMenuExpanded = true
                  await uiStore.populatePoiResultsForCurrentSelection()
                }}
              />
            </div>
          )}
        </div>
        {isSubToolsOpen && (
          <div className='sub-tools-container'>
            <SubTools view={view} />
            {uiStore.isHideMenuBtnVisible && !isCreation && (
              <button
                type='button'
                className='hide-menu-button'
                onClick={() => uiStore.toggleSideBarMenu()}
              >
                <ChevLeftIcon />
              </button>
            )}
          </div>
        )}
        {!selectionStore.menuItem ? null : <div><PopupMenu content={choosePane(selectionStore.menuItem)} /></div>}
        {/* <AnalysisContainer /> */}
      </div>
    )
  }
}
export default withTranslation()(inject('selectionStore', 'uiStore', 'dataStore', 'rightSidebarStore')(Tools))
